import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Eye, Edit3, Loader2, FilePlus2, AlertCircle, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reportTypeLabel = {
  tavr_planning_3mensio: "TAVR · 3mensio",
  generic_cardiac_ct: "Cardiac CT",
};

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

/**
 * Lists clinical PDFs imported for a single study and offers View / Edit
 * (link to /reports/:id) and Delete (admin) controls. Used inside the per-
 * study sheet so users don't have to context-switch to the global Reports
 * page to find a study's reports.
 */
export default function StudyReportsList({ studyId, onImportClick, onChange }) {
  const location = useLocation();
  const backTo = `${location.pathname}${location.search}`;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!studyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/studies/${studyId}/reports`);
      setReports(r.data.reports || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [studyId]);

  const handleDelete = async (id) => {
    if (!confirm("Delete this report? The PDF will also be removed from disk.")) return;
    try {
      await axios.delete(`${API}/reports/${id}`);
      toast.success("Report deleted");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="study-reports-list">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {loading ? "Loading…" : `${reports.length} clinical report${reports.length === 1 ? "" : "s"}`}
        </div>
        <Button
          size="sm"
          onClick={onImportClick}
          className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)] h-8 text-xs"
          data-testid="study-reports-import-btn"
        >
          <FilePlus2 className="w-3.5 h-3.5 mr-1" /> Import PDF
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading reports…
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <div className="text-sm font-medium">No reports yet</div>
            <p className="text-xs text-muted-foreground mt-1 px-6">
              Import a PDF to extract structured fields (Annulus, LVOT, valve sizing, etc.) with AI.
            </p>
          </div>
        ) : (
          reports.map((r, idx) => {
            const ex = r.extracted || {};
            const failed = r.extract_status === "failed";
            const conf = typeof ex?.confidence === "number" ? `${Math.round(ex.confidence * 100)}%` : "—";
            const patientName = ex?.patient_identity?.name;
            return (
              <div
                key={r.id}
                data-testid={`study-report-${idx}`}
                className="border border-border rounded-lg p-3 bg-white hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[var(--xl-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-[var(--xl-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium truncate" title={r.original_filename}>
                        {r.original_filename}
                      </div>
                      {failed ? (
                        <Badge variant="outline" className="text-[10px] text-red-600 border-red-200 gap-1">
                          <AlertCircle className="w-3 h-3" /> failed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          {reportTypeLabel[ex.report_type] || "Report"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 truncate">
                      {patientName ? `${patientName} · ` : ""}
                      AI confidence {conf} · {fmtSize(r.file_size)} ·{" "}
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                      >
                        <Link
                          to={`/reports/${r.id}`}
                          state={{ from: backTo }}
                          data-testid={`study-report-${idx}-view`}
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                      >
                        <Link
                          to={`/reports/${r.id}?edit=1`}
                          state={{ from: backTo }}
                          data-testid={`study-report-${idx}-edit`}
                        >
                          <Edit3 className="w-3 h-3 mr-1" /> Edit
                        </Link>
                      </Button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        data-testid={`study-report-${idx}-delete`}
                        className="ml-auto p-1 text-muted-foreground hover:text-red-600 rounded hover:bg-red-50"
                        title="Delete report"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
